import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, Alert, Chip } from '@mui/material';
import { Science, Assignment, CheckCircle, Pending, Business, People, Today, CalendarMonth, Analytics } from '@mui/icons-material';
import { fetchSamples } from '../store/sampleSlice';
import api from '../services/api';
import { DepartmentChart, TrendChart, StatusPieChart } from '../components/Charts';

const StatCard = ({ title, value, icon, color, onClick, subtitle }) => (
  <Card 
    sx={{ 
      height: '100%',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,1) 100%)',
      transition: 'transform 0.2s ease-in-out',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? {
        transform: 'translateY(-2px)',
        boxShadow: 3
      } : {},
      '&:active': {
        transform: 'scale(0.98)'
      }
    }}
    onClick={onClick}
  >
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 700,
              color: color,
              mb: 0.5
            }}
          >
            {value}
          </Typography>
          <Typography 
            color="textSecondary" 
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 500
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="caption" 
              color="textSecondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box 
          sx={{ 
            color, 
            opacity: 0.8,
            '& svg': {
              fontSize: { xs: '2rem', sm: '2.5rem' }
            }
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.auth.user);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'SuperAdmin') {
      fetchCompanies();
      fetchUsers();
    } else {
      fetchStats();
    }

    // Listen for dashboard refresh events
    const handleRefreshStats = () => {
      if (user?.role !== 'SuperAdmin') {
        fetchStats();
      }
    };

    window.addEventListener('refreshDashboardStats', handleRefreshStats);
    return () => window.removeEventListener('refreshDashboardStats', handleRefreshStats);
  }, [user]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data.companies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const getSampleStats = () => {
    if (!stats) return { total: 0, pending: 0, accepted: 0, completed: 0 };
    return stats.total;
  };

  const getSuperAdminStats = () => {
    return {
      totalCompanies: companies.length,
      activeCompanies: companies.filter(c => c.active).length,
      totalUsers: users.length,
      activeUsers: users.filter(u => u.active).length
    };
  };

  const renderSuperAdminDashboard = () => {
    const stats = getSuperAdminStats();
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Companies"
            value={stats.totalCompanies}
            icon={<Business />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Companies"
            value={stats.activeCompanies}
            icon={<Business />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<People />}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Users"
            value={stats.activeUsers}
            icon={<People />}
            color="success.main"
          />
        </Grid>
      </Grid>
    );
  };

  const handleCardClick = (filterType) => {
    const params = new URLSearchParams();
    
    switch (filterType) {
      case 'pending':
        params.set('statusFilter', 'pending');
        break;
      case 'accepted':
        params.set('statusFilter', 'accepted');
        break;
      case 'completed':
        params.set('statusFilter', 'completed');
        break;
      default:
        // For 'total', no filter is applied
        break;
    }
    
    navigate(`/samples?${params.toString()}`);
  };

  const renderSampleDashboard = () => {
    if (loading) {
      return <Typography>Loading...</Typography>;
    }

    if (!stats) {
      return <Alert severity="error">Failed to load dashboard statistics</Alert>;
    }

    const totalStats = stats.total;
    const todayStats = stats.today;
    const monthStats = stats.thisMonth;

    return (
      <>
        {/* Main Stats */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title={user?.role === 'Admin' ? 'Total Samples' : 'My Samples'}
              value={totalStats.samples}
              icon={<Science />}
              color="primary.main"
              onClick={() => handleCardClick('total')}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title="Pending"
              value={totalStats.pending}
              icon={<Pending />}
              color="warning.main"
              onClick={() => handleCardClick('pending')}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title="Accepted"
              value={totalStats.accepted}
              icon={<Assignment />}
              color="info.main"
              onClick={() => handleCardClick('accepted')}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              title="Completed"
              value={totalStats.completed}
              icon={<CheckCircle />}
              color="success.main"
              onClick={() => handleCardClick('completed')}
            />
          </Grid>
        </Grid>

        {/* Daily & Monthly Stats */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Today color="primary" />
            Today's Activity
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="Created Today"
                value={todayStats.samples}
                subtitle={`${todayStats.pending} pending, ${todayStats.completed} completed`}
                icon={<Science />}
                color="primary.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                title="This Month"
                value={monthStats.samples}
                subtitle={`${monthStats.pending} pending, ${monthStats.completed} completed`}
                icon={<CalendarMonth />}
                color="secondary.main"
              />
            </Grid>
          </Grid>
        </Box>
        
        {/* Analytics Charts */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Analytics color="primary" />
            Analytics Overview
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <StatusPieChart stats={totalStats} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TrendChart last7Days={stats.last7Days} />
            </Grid>
            <Grid item xs={12}>
              <DepartmentChart departmentStats={stats.departmentStats} />
            </Grid>
          </Grid>
        </Box>

        {/* Quick Actions for Mobile */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            {(['Admin', 'Sample Collector'].includes(user?.role)) && (
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:active': { transform: 'scale(0.98)' }
                  }}
                  onClick={() => navigate('/samples/create')}
                >
                  <Science color="primary" sx={{ fontSize: '2rem', mb: 1 }} />
                  <Typography variant="body2" fontWeight={500}>
                    Create Sample
                  </Typography>
                </Card>
              </Grid>
            )}
            <Grid item xs={6}>
              <Card 
                sx={{ 
                  p: 2, 
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:active': { transform: 'scale(0.98)' }
                }}
                onClick={() => navigate('/samples')}
              >
                <Assignment color="primary" sx={{ fontSize: '2rem', mb: 1 }} />
                <Typography variant="body2" fontWeight={500}>
                  View Samples
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
        {user?.company && (
          <Typography variant="subtitle1" color="textSecondary">
            {user.company.name}
          </Typography>
        )}
      </Typography>
      
      {user?.role === 'SuperAdmin' ? renderSuperAdminDashboard() : renderSampleDashboard()}
    </Box>
  );
};

export default Dashboard;